import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  SiteBlackout,
  SiteBlackoutUpdate,
  BlackoutType,
  BLACKOUT_TYPE_LABELS,
} from '../../types/site';
import {
  isBlackoutCurrentlyActive,
  formatBlackoutDate,
  getBlackoutTypeIcon,
  validateBlackoutDraft,
} from '../../utils/blackoutUtils';
import './BlackoutDetailPopover.css';

interface BlackoutDetailPopoverProps {
  blackout: SiteBlackout;
  siteAlias?: string;
  anchorRect: DOMRect;
  onSave: (id: number, update: SiteBlackoutUpdate) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onClose: () => void;
}

interface BlackoutDraft {
  blackout_type: BlackoutType;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  active: boolean;
}

const draftFromBlackout = (b: SiteBlackout): BlackoutDraft => ({
  blackout_type: b.blackout_type,
  title: b.title,
  description: b.description || '',
  start_date: b.start_date,
  end_date: b.end_date,
  active: b.active,
});

// Rough panel heights, used only to decide whether to open below or above the
// clicked cell. The edit form is far taller than the other grid popovers, so
// without this it would run off the bottom on the lower site rows.
const VIEW_HEIGHT_PX = 220;
const EDIT_HEIGHT_PX = 400;
const WIDTH_PX = 320;

const BlackoutDetailPopover: React.FC<BlackoutDetailPopoverProps> = ({
  blackout,
  siteAlias,
  anchorRect,
  onSave,
  onDelete,
  onClose,
}) => {
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [draft, setDraft] = useState<BlackoutDraft>(() => draftFromBlackout(blackout));
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Deactivating or deleting drops the record from the parent's list, which
  // unmounts this popover — so the trailing setState must not run.
  const mounted = useRef(true);
  const finish = useCallback(() => {
    if (mounted.current) setSaving(false);
  }, []);
  useEffect(() => {
    // Set on mount, not just cleared on unmount: StrictMode runs effects
    // mount → cleanup → mount in dev, which would otherwise leave this false.
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const style = (() => {
    const estimatedHeight = mode === 'edit' ? EDIT_HEIGHT_PX : VIEW_HEIGHT_PX;
    const below = anchorRect.bottom + 6;
    const fitsBelow = below + estimatedHeight <= window.innerHeight - 8;
    const top = fitsBelow ? below : Math.max(8, anchorRect.top - estimatedHeight - 6);
    const left = Math.min(Math.max(8, anchorRect.left), window.innerWidth - WIDTH_PX - 8);
    return { top, left, width: WIDTH_PX };
  })();

  const startEditing = () => {
    // Re-seed from the current record so an edit begun after an external
    // refresh starts from fresh values.
    setDraft(draftFromBlackout(blackout));
    setConfirmingDelete(false);
    setError(null);
    setMode('edit');
  };

  const handleSave = async () => {
    const validationError = validateBlackoutDraft(draft);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await onSave(blackout.id, {
        blackout_type: draft.blackout_type,
        title: draft.title.trim(),
        // Sent even when empty so clearing the description persists.
        description: draft.description.trim(),
        start_date: draft.start_date,
        end_date: draft.end_date,
        active: draft.active,
      });
      if (mounted.current) setMode('view');
    } catch (err) {
      setError('Failed to update blackout period');
      console.error('Error updating blackout:', err);
    } finally {
      finish();
    }
  };

  const handleDeactivate = async () => {
    try {
      setSaving(true);
      setError(null);
      // A single-field patch, so no validation — an already-invalid stored
      // record can still be switched off.
      await onSave(blackout.id, { active: false });
    } catch (err) {
      setError('Failed to deactivate blackout period');
      console.error('Error deactivating blackout:', err);
    } finally {
      finish();
    }
  };

  const handleDelete = async () => {
    try {
      setSaving(true);
      setError(null);
      await onDelete(blackout.id);
    } catch (err) {
      setError('Failed to delete blackout period');
      console.error('Error deleting blackout:', err);
    } finally {
      finish();
    }
  };

  const blocking = isBlackoutCurrentlyActive(blackout);

  return (
    <>
      <div className="blackout-popover-backdrop" onClick={onClose} />
      <div className="blackout-popover" style={style}>
        <div className="blackout-popover-header">
          <span className="blackout-popover-icon">{getBlackoutTypeIcon(blackout.blackout_type)}</span>
          <span className="blackout-popover-type">
            {BLACKOUT_TYPE_LABELS[blackout.blackout_type]}
          </span>
          {blocking && <span className="blackout-popover-active-badge">Active now</span>}
          {siteAlias && <span className="blackout-popover-site">Site {siteAlias}</span>}
        </div>

        {error && <div className="blackout-popover-error">{error}</div>}

        {mode === 'view' ? (
          <>
            <div className="blackout-popover-title">{blackout.title}</div>
            <div className="blackout-popover-dates">
              {formatBlackoutDate(blackout.start_date)} — {formatBlackoutDate(blackout.end_date)}
            </div>
            <div className="blackout-popover-hint">
              Bookable again on {formatBlackoutDate(blackout.end_date)}
            </div>
            {blackout.description && (
              <div className="blackout-popover-description">{blackout.description}</div>
            )}

            {confirmingDelete ? (
              <div className="blackout-popover-confirm-delete">
                <span>Delete this blackout?</span>
                <span className="blackout-popover-confirm-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setConfirmingDelete(false)}
                    disabled={saving}
                  >
                    Keep
                  </button>
                  <button
                    className="btn btn-sm blackout-popover-danger"
                    onClick={handleDelete}
                    disabled={saving}
                  >
                    {saving ? 'Deleting...' : 'Delete'}
                  </button>
                </span>
              </div>
            ) : (
              <>
                <div className="blackout-popover-actions">
                  <button className="btn btn-secondary btn-sm" onClick={startEditing} disabled={saving}>
                    Edit
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={handleDeactivate} disabled={saving}>
                    {saving ? 'Working...' : 'Deactivate'}
                  </button>
                  <button
                    className="btn btn-sm blackout-popover-danger"
                    onClick={() => setConfirmingDelete(true)}
                    disabled={saving}
                  >
                    Delete
                  </button>
                </div>
                <div className="blackout-popover-hint">
                  Deactivated blackouts can be brought back from Site Management.
                </div>
              </>
            )}
          </>
        ) : (
          <div className="blackout-popover-form">
            <div className="blackout-popover-field">
              <label htmlFor="blackout-popover-type">Type</label>
              <select
                id="blackout-popover-type"
                value={draft.blackout_type}
                onChange={(e) => setDraft({ ...draft, blackout_type: e.target.value as BlackoutType })}
              >
                {Object.entries(BLACKOUT_TYPE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {getBlackoutTypeIcon(key as BlackoutType)} {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="blackout-popover-row">
              <div className="blackout-popover-field">
                <label htmlFor="blackout-popover-start">Start date</label>
                <input
                  id="blackout-popover-start"
                  type="date"
                  value={draft.start_date}
                  onChange={(e) => setDraft({ ...draft, start_date: e.target.value })}
                />
              </div>
              <div className="blackout-popover-field">
                <label htmlFor="blackout-popover-end">End date</label>
                <input
                  id="blackout-popover-end"
                  type="date"
                  value={draft.end_date}
                  onChange={(e) => setDraft({ ...draft, end_date: e.target.value })}
                />
              </div>
            </div>

            <div className="blackout-popover-field">
              <label htmlFor="blackout-popover-title">Title</label>
              <input
                id="blackout-popover-title"
                type="text"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="e.g., Scheduled Maintenance"
              />
            </div>

            <div className="blackout-popover-field">
              <label htmlFor="blackout-popover-description">Description (optional)</label>
              <input
                id="blackout-popover-description"
                type="text"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="e.g., Plumbing repair"
              />
            </div>

            <label className="blackout-popover-checkbox">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
              />
              Active
            </label>

            <div className="blackout-popover-actions">
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => { setMode('view'); setError(null); }}
                disabled={saving}
              >
                Cancel
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BlackoutDetailPopover;
